import { CancelRounded, SaveRounded } from '@mui/icons-material'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import FormHelperText from '@mui/material/FormHelperText'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Trash } from '@phosphor-icons/react'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { getLocaleFromFullDate } from 'common/libs/fechas'
import FilesHelper from 'common/libs/Files'
import dayjs from 'dayjs'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import useFetchOptionsHojasGastoLin from '../hooks/useFetchOptionsHojasGastoLin'
import { createHojaGastoLin, editHojaGastoLin } from '../lib/HojasGastoLinController'

const ExpenseModal = ({ id_hoja, updateState, lineInfo = {}, fechaInicio, fechaFin }) => {
  const { handleModal } = useContext(CustomModalContext)

  dayjs.locale('es')
  const date = lineInfo?.fecha ? dayjs(lineInfo.fecha) : dayjs()
  const startDate = dayjs(fechaInicio)
  const endDate = dayjs(fechaFin)
  const isTodayInRange = date.isBetween(startDate, endDate, 'day', '[]')

  const [fecha, setFecha] = useState(isTodayInRange ? date : startDate)

  const [gruposGastos, setGruposGastos] = useState([])
  const [selectedGrupo, setSelectedGrupo] = useState({})
  const [grupoidx, setGrupoidx] = useState(0)

  const [selectedConcept, setSelectedConcepto] = useState({})
  const [conceptoidx, setConceptoidx] = useState(null)

  const [descripcion, setDescripcion] = useState(lineInfo?.descripcion || '')
  const [observaciones, setObservaciones] = useState(lineInfo?.observaciones || '')
  const [cantidad, setCantidad] = useState(lineInfo?.cantidad || 1)
  const [precio, setPrecio] = useState(lineInfo?.precio || 0)
  const [importe, setImporte] = useState(lineInfo?.importe || 0)

  const [documents, setDocuments] = useState(lineInfo?.documentos || [])

  const [addError, setAddError] = useState(false)
  const [fechaError, setFechaError] = useState('')
  const [grupoGastoError, setGrupoGastoError] = useState('')
  const [documentsError, setDocumentsError] = useState('')
  const [descripcionError, setDescripcionError] = useState('')
  const [conceptoError, setConceptoError] = useState('')
  const [precioError, setPrecioError] = useState('')
  const [cantidadError, setCantidadError] = useState('')
  const [openBackdrop, setOpenBackdrop] = useState(false)
  const [contentBackdrop, setContentBackdrop] = useState('')

  const { response, error, loading } = useFetchOptionsHojasGastoLin()

  useEffect(() => {
    if (response?.grupos_gastos && response?.grupos_gastos.length > 0) {
      setGruposGastos(response.grupos_gastos)
      if (lineInfo?.grupo_gastos) {
        const grupoIndex = response.grupos_gastos.findIndex(
          (grupo) => grupo.codigo === lineInfo?.grupo_gastos,
        )
        setGrupoidx(grupoIndex)
        setSelectedGrupo(response.grupos_gastos[grupoIndex])

        if (lineInfo?.concepto) {
          const conceptIndex = response.grupos_gastos[grupoIndex]?.conceptos.findIndex(
            (concept) => concept.concepto === lineInfo?.concepto,
          )
          setConceptoidx(conceptIndex)
          setSelectedConcepto(response.grupos_gastos[grupoIndex]?.conceptos[conceptIndex])
        }
      } else {
        setGrupoidx(0)
        setSelectedGrupo(response.grupos_gastos[0])
        setConceptoidx(0)
        setSelectedConcepto(response.grupos_gastos[0]?.conceptos[0])
      }
    }
  }, [response])

  const resetState = () => {
    setFecha(isTodayInRange ? date : startDate)
    setSelectedGrupo({})
    setGrupoidx(0)
    setSelectedConcepto({})
    setConceptoidx(0)
    setDescripcion('')
    setObservaciones('')
    setCantidad(1)
    setPrecio(0)
    setImporte(0)
    setDocuments([])
  }

  const handleClick = async () => {
    try {
      setOpenBackdrop(true)
      setContentBackdrop('Guardando gasto...')

      let error = false
      if (
        fecha &&
        (startDate.isBefore(fecha) || startDate.isSame(fecha)) &&
        (endDate.isAfter(fecha) || endDate.isSame(fecha))
      ) {
        setFechaError('')
      } else {
        error = true
        setFechaError(
          `La fecha es obligatoria y debe estar entre ${getLocaleFromFullDate(fechaInicio, false)} y ${getLocaleFromFullDate(fechaFin, false)}`,
        )
      }

      if (!selectedGrupo) {
        error = true
        setGrupoGastoError('El grupo de gastos es obligatorio')
      } else {
        setGrupoGastoError('')
      }

      if (
        gruposGastos[grupoidx].conceptos[conceptoidx].obligar_adjunto === 'S' &&
        documents.length < 1
      ) {
        error = true
        setDocumentsError('Para este concepto los documentos son obligatorios')
      } else {
        setDocumentsError('')
      }

      if (!descripcion) {
        error = true
        setDescripcionError('La descripción es obligatoria')
      } else {
        setDescripcionError('')
      }

      if (!selectedConcept) {
        setConceptoError('El concepto es obligatorio')
        error = true
      } else {
        setConceptoError('')
      }

      if (parseFloat(precio) <= 0) {
        error = true
        setPrecioError('El precio no puede ser menor o igual a cero')
      } else {
        setPrecioError('')
      }
      if (cantidad <= 0) {
        error = true
        setCantidadError('La cantidad no puede ser menor o igual a cero')
      } else {
        setCantidadError('')
      }

      if (error) {
        setAddError(true)
        setOpenBackdrop(false)
        return
      } else {
        setAddError(false)
      }

      let res = {}
      if (lineInfo?.id_lin) {
        res = await editHojaGastoLin({
          id_hoja: parseInt(id_hoja),
          id_lin: lineInfo.id_lin,
          fecha: fecha.add(fecha.utcOffset(), 'minutes').toISOString().split('T')[0],
          grupoGastos: selectedGrupo.codigo,
          concepto: selectedConcept.concepto,
          descripcion,
          cantidad,
          precio,
          observaciones,
          documentos: documents,
        })
      } else {
        res = await createHojaGastoLin({
          id_hoja: parseInt(id_hoja),
          fecha: fecha.add(fecha.utcOffset(), 'minutes').toISOString().split('T')[0],
          grupoGastos: selectedGrupo.codigo,
          concepto: selectedConcept.concepto,
          descripcion,
          cantidad,
          precio,
          observaciones,
          documentos: documents,
        })
      }
      enqueueSnackbar(`Línea ${lineInfo.id_lin ? 'editada' : 'creada'} correctamente`, {
        variant: 'success',
      })
      updateState(res)
      handleClose()
      setOpenBackdrop(false)
    } catch (error) {
      setOpenBackdrop(false)
      enqueueSnackbar('No se ha podido crear la línea')
    }
  }

  const handleClose = () => {
    resetState()
    handleModal({ enabled: false })
  }

  useEffect(() => {
    if (cantidad && precio) {
      setImporte(parseFloat(cantidad) * parseFloat(precio))
    } else {
      setImporte(0)
    }
  }, [cantidad, precio])

  useEffect(() => {
    if (!loading) {
      setPrecio(
        lineInfo?.concepto === selectedConcept?.concepto
          ? lineInfo?.precio
          : (selectedConcept?.precio ?? 0),
      )
    }
  }, [selectedConcept])

  const convert = async (acceptedFiles) => {
    let doc = []
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i]
      const result = await FilesHelper.fileToBase64(file)
      doc = [...doc, { nombre_archivo: result.name, data: result.base64 }]
    }
    return doc
  }

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const data = await convert(acceptedFiles)
      setDocuments([...documents, ...data])
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDocuments],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 999,
  })

  const files = documents.map((file) => {
    return <li key={file.nombre_archivo}>{file.nombre_archivo}</li>
  })

  const precioDisabled = gruposGastos
    ? gruposGastos[grupoidx]?.conceptos
      ? gruposGastos[grupoidx]?.conceptos[conceptoidx]?.precio
      : false
    : false

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Backdrop
        sx={{
          flexDirection: 'column',
          gap: '20px',
          borderRadius: '20px',
          zIndex: (theme) => theme.zIndex.drawer - 1,
        }}
        open={openBackdrop}
      >
        <Paper
          sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}
        >
          <CircularProgress color="primary" />
          <Typography>{contentBackdrop}</Typography>
        </Paper>
      </Backdrop>
      <Box
        className="minimal-scrollbar"
        sx={{
          bgcolor: 'background.paper',
          border: '1px',
          borderRadius: 5,
          boxShadow: 24,
          p: 4,
          maxHeight: 'calc(100vh - (3*var(--mui-spacing)))',
          overflow: 'auto',
        }}
      >
        <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
          {lineInfo.id_hoja ? 'Editar' : 'Nuevo'} Gasto
        </Typography>
        <Divider sx={{ mb: 4 }} />
        {error ? (
          <Typography>
            Lo sentimos se ha producido un error. Inténtelo de nuevo más tarde.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {addError ? (
              <Typography sx={{ color: 'red', width: '100%' }}>
                Algunos datos son incorrectos
              </Typography>
            ) : null}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
              }}
            >
              <Box sx={{ flex: '1 0 auto' }}>
                {loading ? (
                  <DayCalendarSkeleton />
                ) : (
                  <DateCalendar
                    value={fecha}
                    onChange={(e) => setFecha(e)}
                    views={['day']}
                    minDate={startDate}
                    maxDate={endDate}
                  />
                )}
                {fechaError ? (
                  <Typography sx={{ color: 'red', width: '100%' }}>{fechaError}</Typography>
                ) : null}
              </Box>
              <Box sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl sx={{ width: '100%' }} error={grupoGastoError ? true : false}>
                  <Typography
                    variant="h3"
                    sx={{ mb: 1, fontSize: '1rem', ml: 0, fontWeight: '500' }}
                  >
                    Grupo gastos <sup>*</sup>
                  </Typography>
                  <Select
                    value={grupoidx}
                    placeholder={loading ? 'Cargando...' : 'Seleccione grupo de gasto'}
                    onChange={(e) => {
                      if (gruposGastos) {
                        setSelectedGrupo(gruposGastos[e.target.value])
                        setGrupoidx(e.target.value)
                        setSelectedConcepto(gruposGastos[e.target.value]?.conceptos[0])
                      }
                    }}
                  >
                    {gruposGastos?.map((grupo, idx) => (
                      <MenuItem value={idx} key={idx} data-grupo={grupo.codigo}>
                        {grupo.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{grupoGastoError}</FormHelperText>
                </FormControl>
                <FormControl sx={{ width: '100%' }} error={conceptoError ? true : false}>
                  <Typography
                    variant="h3"
                    sx={{ mb: 1, fontSize: '1rem', ml: 0, fontWeight: '500' }}
                  >
                    Concepto <sup>*</sup>
                  </Typography>
                  <Select
                    value={conceptoidx}
                    placeholder={loading ? 'Cargando...' : 'Seleccione concepto'}
                    onChange={(e) => {
                      setSelectedConcepto(gruposGastos[grupoidx]?.conceptos[e.target.value])
                      setConceptoidx(e.target.value)
                    }}
                  >
                    {gruposGastos[grupoidx]?.conceptos?.map((concepto, idx) => (
                      <MenuItem value={idx} key={idx}>
                        {concepto.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{conceptoError}</FormHelperText>
                </FormControl>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{ mb: 1, fontSize: '1rem', ml: 0, fontWeight: '500' }}
                  >
                    Adjuntos
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '2px dashed',
                      backgroundColor: 'background.default',
                      width: '100%',
                    }}
                  >
                    <div {...getRootProps({ className: 'dropzone' })}>
                      <input {...getInputProps()} />

                      <SaveAltIcon />
                      <p>Arrastre aquí ficheros o haga click para seleccionar</p>
                    </div>
                  </Box>
                  {documents && documents.length > 0 ? (
                    documents.map((file, idx) => {
                      return (
                        <Box
                          key={file.nombre_archivo || idx}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <Typography>{file.nombre_archivo}</Typography>
                          <IconButton onClick={() => setDocuments(idx)}>
                            <Trash />
                          </IconButton>
                        </Box>
                      )
                    })
                  ) : (
                    <></>
                  )}
                  {documentsError ? (
                    <Typography sx={{ color: 'red', width: '100%' }}>{documentsError}</Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Box sx={{ width: '100%' }}>
              <Typography variant="h3" sx={{ mb: 1, fontSize: '1rem', ml: 0, fontWeight: '500' }}>
                Descripción <sup>*</sup>
              </Typography>
              <TextField
                error={descripcionError ? true : false}
                variant="standard"
                value={descripcion}
                type="text"
                onChange={(e) => setDescripcion(e.target.value)}
                required
                fullWidth
                placeholder="Descripción del gasto"
              />
              {descripcionError ? <Typography color="error">{descripcionError}</Typography> : null}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <Box sx={{ flex: '1 0 50%' }}>
                <Typography variant="h3" sx={{ mb: 1, fontSize: '1rem', ml: 0 }}>
                  Observaciones
                </Typography>
                <TextField
                  variant="standard"
                  value={observaciones}
                  type="text"
                  onChange={(e) => setObservaciones(e.target.value)}
                  fullWidth
                  multiline
                  rows={16}
                />
              </Box>
              <Box sx={{ flex: '1 0 50%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ width: '100%' }}>
                  <Typography
                    variant="h3"
                    sx={{ mb: 1, fontSize: '1rem', ml: 0, fontWeight: '500' }}
                  >
                    Cantidad <sup>*</sup>
                  </Typography>
                  <TextField
                    error={cantidadError ? true : false}
                    variant="standard"
                    value={cantidad}
                    type="number"
                    onChange={(e) => setCantidad(e.target.value)}
                    required
                    fullWidth
                    placeholder="Cantidad"
                    sx={{
                      '& input': {
                        textAlign: 'end',
                        fontSize: '3rem',
                      },
                      'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      'input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                    }}
                  />
                  {cantidadError ? <Typography color="error">{cantidadError}</Typography> : null}
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography
                    variant="h3"
                    sx={{ mb: 1, fontSize: '1rem', ml: 0, fontWeight: '500' }}
                  >
                    Precio <sup>*</sup>
                  </Typography>
                  <TextField
                    error={precioError ? true : false}
                    variant="standard"
                    value={precio}
                    type="number"
                    onChange={(e) => {
                      if (!e.target.value) {
                        setPrecio(e.target.value)
                        return
                      }
                      if (
                        !gruposGastos[grupoidx]?.conceptos[conceptoidx]?.precio_maximo ||
                        parseFloat(gruposGastos[grupoidx]?.conceptos[conceptoidx]?.precio_maximo) >=
                          parseFloat(e.target.value)
                      ) {
                        setPrecio(e.target.value)
                      }
                    }}
                    disabled={precioDisabled}
                    required
                    fullWidth
                    placeholder="Precio"
                    sx={{
                      '& input': {
                        textAlign: 'end',
                        fontSize: '3rem',
                      },
                      'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      'input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                    }}
                  />
                  {precioError ? <Typography color="error">{precioError}</Typography> : null}
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography
                    variant="h3"
                    sx={{ mb: 1, fontSize: '1rem', ml: 0, fontWeight: '500' }}
                  >
                    Importe <sup>*</sup>
                  </Typography>
                  <TextField
                    variant="standard"
                    value={importe}
                    type="number"
                    onChange={(e) => setImporte(e.target.value)}
                    disabled
                    fullWidth
                    placeholder="Importe"
                    sx={{
                      '& input': {
                        textAlign: 'end',
                        fontSize: '4rem',
                      },
                      'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      'input[type=number]': {
                        MozAppearance: 'textfield',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleClose}
                color="error"
                startIcon={<CancelRounded />}
              >
                <Typography sx={{ display: { xs: 'none', md: 'inherit' } }}>Cancelar</Typography>
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleClick}
                color="success"
                fullWidth
                startIcon={<SaveRounded />}
              >
                <Typography sx={{ display: { xs: 'none', md: 'inherit' } }}>Guardar</Typography>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default ExpenseModal
